/**
 * formatErrorDetail
 * ─────────────────
 * Render a FastAPI-style error response into a friendly string.
 *
 * FastAPI returns three shapes that the UI may surface:
 *   1. `{ detail: "Some message" }`                                 (HTTPException)
 *   2. `{ detail: { msg: "...", code: "..." } }`                    (custom)
 *   3. `{ detail: [{ loc: [...], msg: "...", type: "..." }, ...] }` (validation 422)
 *
 * `new Error(detail)` against shapes 2 or 3 produces "[object Object]" — this
 * helper keeps human-readable messages without leaking internals.
 */
export function formatErrorDetail(detail: unknown, fallback: string): string {
  if (detail == null) return fallback;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        if (typeof d === "string") return d;
        if (d && typeof d === "object" && "msg" in d) {
          const msg = String((d as { msg: unknown }).msg ?? "");
          const loc = (d as { loc?: unknown[] }).loc;
          if (Array.isArray(loc) && loc.length > 1) {
            const field = String(loc[loc.length - 1]);
            return `${field}: ${msg}`;
          }
          return msg;
        }
        return "";
      })
      .filter(Boolean);
    return parts.length > 0 ? parts.join("; ") : fallback;
  }

  if (typeof detail === "object" && "msg" in (detail as object)) {
    return String((detail as { msg: unknown }).msg ?? fallback);
  }

  return fallback;
}
